"use client";

import { Fragment, type ReactNode } from "react";
import { useLocale } from "next-intl";
import { Link, Typography } from "@mui/material";
import type { SupportedLocale } from "@/config/locale-config";
import type {
	StrapiBlocksInlineNode,
	StrapiBlocksNode,
	StrapiBlocksTextNode,
} from "@/types/strapi";
import { resolveMarketingHref } from "@/utils/cms/marketing-href";

const renderText = (node: StrapiBlocksTextNode, key: number): ReactNode => {
	let content: ReactNode = node.text;

	if (node.bold) content = <strong>{content}</strong>;
	if (node.italic) content = <em>{content}</em>;
	if (node.underline) content = <u>{content}</u>;

	return <Fragment key={key}>{content}</Fragment>;
};

const renderInline = (
	nodes: StrapiBlocksInlineNode[],
	locale: SupportedLocale
): ReactNode[] =>
	nodes.map((node, index) => {
		if (node.type === "text") return renderText(node, index);

		const href = resolveMarketingHref(node.url, locale);
		const label = node.children.map((child) => child.text).join("");

		// Links that fail sanitisation still render their label, just inert.
		if (!href) return <Fragment key={index}>{label}</Fragment>;

		return (
			<Link
				key={index}
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				underline="always"
			>
				{label}
			</Link>
		);
	});

type StrapiBlocksProps = {
	nodes: StrapiBlocksNode[];
	variant?: "body1" | "body2";
};

/** Minimal renderer for Strapi's rich-text Blocks format. */
export const StrapiBlocks = ({
	nodes,
	variant = "body1",
}: StrapiBlocksProps) => {
	const locale = useLocale() as SupportedLocale;

	return (
		<>
			{nodes.map((node, index) => {
				if (node.type === "list") {
					const ListTag =
						node.format === "ordered" ? "ol" : ("ul" as const);

					return (
						<Typography
							key={index}
							component={ListTag}
							variant={variant}
							sx={{ paddingInlineStart: 3, margin: 0 }}
						>
							{node.children.map((item, itemIndex) => (
								<li key={itemIndex}>
									{renderInline(item.children, locale)}
								</li>
							))}
						</Typography>
					);
				}

				if (node.type === "heading") {
					return (
						<Typography
							key={index}
							variant={variant}
							component="p"
							sx={{
								fontWeight: "medium",
							}}
						>
							{renderInline(node.children, locale)}
						</Typography>
					);
				}

				return (
					<Typography key={index} variant={variant}>
						{renderInline(node.children, locale)}
					</Typography>
				);
			})}
		</>
	);
};
